"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Customer,
  CustomerService,
  CustomerStatusCode,
  CustomerType,
} from "../services/customer-service";
import CustomerTable, { CustomerRow } from "../components/customer-table";
import CustomerCreateModal, {
  CustomerFormValues,
} from "../components/customer-create-form";
import CustomerDetailModal from "../components/customer-detail-modal";
import CustomerImportModal from "../components/customer-import-modal";
import CustomerAccountModal from "../components/customer-account-modal";
import CustomerSlaConfigModal from "../components/customer-sla-config-modal";
import CustomerSlaOverrideService from "../services/customer-sla-override-service";
import {
  CustomerAccountResult,
  CustomerAccountService,
  CustomerAccountSummary,
} from "../services/customer-account-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import {CustomerTypeSelect} from "../components/customer-type-select";
import UserService, { UserStatusCode } from "../../user/services/user-service";
function useDebounce<T>(value: T, delay = 500): T {
  const [val, setVal] = useState(value);
  useEffect(() => { const t = setTimeout(() => setVal(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return val;
}
const CustomerIndexPage: React.FC = () => {
  // STATE
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [total, setTotal] = useState<number>(0); 
  const [usersMap, setUsersMap] = useState<Map<number, string>>(new Map());
  const { fetch: fetchUsers } = UserService.list();

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const res = await fetchUsers({ status: UserStatusCode.ACTIVE, limit: 500 });
        if (mounted && res && (res as any).data?.list) {
          const m = new Map<number, string>();
          for (const u of (res as any).data.list) {
            if (u.id) m.set(Number(u.id), u.name);
          }
          setUsersMap(m);
        }
      } catch (err) {
        console.error("Lỗi tải users map:", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [tab, setTab] = useState<"active" | "inactive" | "all">("active");
  const [openCreate, setOpenCreate] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [editRow, setEditRow] = useState<CustomerRow | null>(null);
  const [detailRow, setDetailRow] = useState<CustomerRow | null>(null);
  const [detailData, setDetailData] = useState<Customer | undefined>();
  const [keySearch, setKeySearch] = useState<string>("");
  const [customerTypeFilter, setCustomerTypeFilter] = useState<number>(0);
  /** Khách đang thao tác tài khoản app, kèm việc đang làm; `null` = modal đóng. */
  const [accountRow, setAccountRow] = useState<{
    row: CustomerRow;
    mode: "issue" | "reset";
    /** ID tài khoản, chỉ có khi đặt lại mật khẩu. */
    accountId?: string;
    username?: string;
  } | null>(null);
  /** Khách đang mở modal cấu hình SLA riêng; `null` = modal đóng. */
  const [slaConfigRow, setSlaConfigRow] = useState<CustomerRow | null>(null);
  /**
   * `customers.id` → tài khoản app tương ứng. Khách không có trong map là chưa được cấp.
   *
   * Khoá là CHUỖI: `customers.id` là `bigint` nên API trả ra chuỗi, dù `CustomerRow.id`
   * khai kiểu `number`. Khoá bằng số thì mọi lần tra đều trượt.
   *
   * `undefined` = chưa tra được; lúc đó bảng vẫn hiện nút "Cấp tài khoản".
   */
  const [accountMap, setAccountMap] = useState<Map<string, CustomerAccountSummary> | undefined>();
  /**
   * `customers.id` của khách ĐANG có cấu hình SLA riêng.
   * Khoá là CHUỖI — cùng lý do với `accountMap`: `customers.id` là `bigint`, API tra hàng loạt trả
   * ra số/chuỗi lẫn lộn tuỳ chặng, so bằng number thì trượt và mọi khách lại hiện "Dùng chung".
   * `undefined` = chưa tra được → cột "SLA riêng" hiện "—" thay vì "Dùng chung" (tránh báo sai).
   */
  const [slaOverrideIds, setSlaOverrideIds] = useState<Set<string> | undefined>();

  // API HOOKS
  const { fetch: fetchList, loading: loadingList } = CustomerService.list();
  const { fetch: countTabs } = CustomerService.countTabs();
  const { fetch: createCustomer, loading: creating } = CustomerService.create();
  const { fetch: updateCustomer, loading: updating } = CustomerService.update();
  const { fetch: lookupSlaOverrides } = CustomerSlaOverrideService.lookupHasOverride();
  const { fetch: fetchDetail, loading: loadingDetail } = CustomerService.detail();
  const { fetch: issueAccount, loading: issuingAccount } = CustomerAccountService.issue();
  const { fetch: resetAccountPassword, loading: resettingAccount } =
    CustomerAccountService.resetPassword();
  const { fetch: lookupAccounts } = CustomerAccountService.lookupByCustomers();

  // Giữ trong ref để effect tra tài khoản không chạy lại mỗi lần component render lại.
  const lookupRef = useRef(lookupAccounts);
  useEffect(() => {
    lookupRef.current = lookupAccounts;
  }, [lookupAccounts]);
  const debouncedSearch = useDebounce(keySearch, 500);
  const listRef = useRef(fetchList);
  useEffect(() => {
    listRef.current = fetchList;
  }, [fetchList]);

  const countRef = useRef(countTabs);
  useEffect(() => {
    countRef.current = countTabs;
  }, [countTabs]);

  // Counts cho 2 tab
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await countRef.current({
        key_search: debouncedSearch,
        type_customer: customerTypeFilter || undefined,
      });
      if (cancelled || !res || !isSuccess(res)) return;
      const d: any = res.data || {};
      setActiveCount(Number(d.total_active ?? 0));
      setInactiveCount(Number(d.total_inactive ?? 0));
      setTotalCount(Number(d.total ?? 0));
    })();
    return () => { cancelled = true; };
  }, [debouncedSearch, customerTypeFilter, rows]);

  // LOAD DATA
  const load = useCallback(async () => {
    // Filter status theo tab để backend phân trang đúng
    const statusFilter = tab === "active"
      ? CustomerStatusCode.ACTIVE
      : tab === "inactive"
        ? CustomerStatusCode.INACTIVE
        : undefined;
    const res = await listRef.current({
      key_search: debouncedSearch,
      page,
      limit,
      type_customer: customerTypeFilter || undefined,
      status: statusFilter,
    });
  
    if (res && isSuccess(res)) {
      const dataBlock: any = (res as any).data ?? {};
      const apiData = Array.isArray(dataBlock.list) ? dataBlock.list : [];
  
      setRows(
        apiData.map((c: any) => ({
          id: c.id,
          code: c.code ?? "",
          name: c.name,
          phone: c.phone,
          address: c.address,
          email: c.email ?? "",
          total_spent: Number(c.total_spent ?? 0),
          completed_orders: Number(c.completed_orders ?? 0),
          type_customer: Number(c.type_customer ?? CustomerType.ENTERPRISE),
          statusCode: Number(c.status) as CustomerStatusCode,
          company_id: Number(c.company_id ?? 0),
          lead_id: String(c.lead_id ?? "0"),
          created_at: c.created_at,
          updated_at: c.updated_at,
          discount_percent: Number(c.discount_percent ?? 0),
          default_payment_method: Number(c.default_payment_method ?? 1),
          requires_counting: Number(c.requires_counting ?? 0),
          sale_id: c.sale_id != null ? Number(c.sale_id) : null,
          saleName: (c.sale_id ? usersMap.get(Number(c.sale_id)) : null) || c.saleName || c.sale?.name || null,
          sale: c.sale ?? (c.sale_id && usersMap.get(Number(c.sale_id)) ? { id: Number(c.sale_id), name: usersMap.get(Number(c.sale_id))! } : null),
          lat: c.lat != null ? Number(c.lat) : null,
          lng: c.lng != null ? Number(c.lng) : null,
        }))
      );
  
      const totalRaw = dataBlock.total ?? dataBlock.total_record ?? dataBlock.totalRecord ?? dataBlock.totalRecords;
      setTotal(Number(totalRaw ?? apiData.length));
    } else {
      showToast({
        message: res?.message ?? "Không thể tải danh sách khách hàng",
        type: "error",
      });
    }
  }, [debouncedSearch, page, limit, customerTypeFilter, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Sau khi có danh sách khách, hỏi user service xem khách nào đã có tài khoản app.
   *
   * Ghép ở đây thay vì bắt customer service gọi chéo sang user service: giữ hai service
   * độc lập. Nếu user service trục trặc thì danh sách khách vẫn hiện đầy đủ, chỉ là cột
   * thao tác không có nút tài khoản.
   */
  useEffect(() => {
    if (!rows.length) {
      setAccountMap(new Map());
      return;
    }

    let cancelled = false;

    (async () => {
      const res = await lookupRef.current(rows.map((row) => row.id));
      if (cancelled) return;

      if (res && isSuccess(res)) {
        setAccountMap(
          new Map((res.data ?? []).map((account) => [String(account.customer_id), account])),
        );
      } else {
        // Bảng vẫn hiện nút "Cấp tài khoản" khi không biết trạng thái, nên nhân viên
        // không bị mất chức năng.
        console.warn(
          "[customer] Không tra được trạng thái tài khoản app:",
          res?.message ?? "không có phản hồi",
        );
        setAccountMap(undefined);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rows]);

  // Giữ trong ref để effect tra SLA override không chạy lại mỗi lần component render lại.
  const lookupSlaRef = useRef(lookupSlaOverrides);
  useEffect(() => {
    lookupSlaRef.current = lookupSlaOverrides;
  }, [lookupSlaOverrides]);

  /** Sau khi có danh sách khách, tra order-service xem khách nào đang có cấu hình SLA riêng. */
  useEffect(() => {
    if (!rows.length) {
      setSlaOverrideIds(new Set());
      return;
    }

    let cancelled = false;

    (async () => {
      const res = await lookupSlaRef.current(rows.map((row) => row.id));
      if (cancelled) return;

      if (res && isSuccess(res)) {
        setSlaOverrideIds(new Set((res.data ?? []).map((id) => String(id))));
      } else {
        // Giống accountMap: không toast (dữ liệu phụ), chỉ log — cột tự hiện "—" khi không tra được.
        console.warn(
          "[customer] Không tra được trạng thái SLA riêng:",
          res?.message ?? "không có phản hồi",
        );
        setSlaOverrideIds(undefined);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rows]);

  // Backend đã filter theo status → rows là data của tab hiện tại, kết hợp tên NV Sale từ usersMap
  const filteredRows = useMemo(() => {
    return rows.map((r) => {
      const sName = r.sale_id ? usersMap.get(Number(r.sale_id)) : null;
      return {
        ...r,
        saleName: sName || r.saleName || r.sale?.name || null,
        sale: r.sale ?? (r.sale_id && sName ? { id: Number(r.sale_id), name: sName } : null),
      };
    });
  }, [rows, usersMap]);

  // Reset page on tab change for better UX
  useEffect(() => {
    setPage(1);
  }, [tab]);

  // CREATE
  const onCreate = async (v: CustomerFormValues): Promise<boolean> => {
    const payload = {
      company_id: 2000,
      name: v.name.trim(),
      phone: v.phone.trim(),
      email: v.email.trim(),
      address: v.address.trim(),
      type_customer: v.type_customer ?? CustomerType.ENTERPRISE,
      status: v.isActive
        ? CustomerStatusCode.ACTIVE
        : CustomerStatusCode.INACTIVE,
      total_spent: 0,
      completed_orders: 0,
      discount_percent: v.discount_percent,
      default_payment_method: v.default_payment_method,
      requires_counting: v.requires_counting,
      sale_id: v.sale_id ?? null,
      // Tạo mới: chỉ gửi toạ độ khi có nhập.
      ...(v.lat != null && v.lng != null ? { lat: v.lat, lng: v.lng } : {}),
    };

    const res = await createCustomer(payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Tạo khách hàng thành công", type: "success" });
      void load();
      return true;
    }

    showToast({
      message: res?.message ?? "Không thể tạo khách hàng",
      type: "error",
    });
    return false;
  };

  // EDIT
  const onEditSubmit = async (v: CustomerFormValues): Promise<boolean> => {
    if (!editRow) return false;
    const payload = {
      name: v.name.trim(),
      phone: v.phone.trim(),
      email: v.email.trim(),
      address: v.address.trim(),
      type_customer: v.type_customer ?? CustomerType.ENTERPRISE,
      status: v.isActive
        ? CustomerStatusCode.ACTIVE
        : CustomerStatusCode.INACTIVE,
      discount_percent: v.discount_percent,
      default_payment_method: v.default_payment_method,
      requires_counting: v.requires_counting,
      sale_id: v.sale_id !== undefined ? (v.sale_id ?? null) : null,
      // Form chỉ trả `code` khi người dùng đổi mã.
      ...(v.code !== undefined ? { code: v.code } : {}),
      // Chỉ gửi toạ độ khi người dùng THỰC SỰ đổi (kể cả xoá = null) — gửi lại y nguyên sẽ làm BE ghi đè
      // nguồn toạ độ thành "ghim tay" dù toạ độ vốn lấy từ nguồn khác.
      ...(v.lat !== ((editRow as any).lat ?? null) || v.lng !== ((editRow as any).lng ?? null)
        ? { lat: v.lat, lng: v.lng }
        : {}),
    };

    const res = await updateCustomer(editRow.id, payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Cập nhật khách hàng thành công", type: "success" });
      setEditRow(null);
      void load();
      return true;
    }

    showToast({
      message: res?.message ?? "Không thể cập nhật khách hàng",
      type: "error",
    });
    return false;
  };

  // DETAIL
  const handleViewDetail = async (row: CustomerRow) => {
    setDetailRow(row);
    setDetailData(undefined);
    const res = await fetchDetail(row.id);
    if (res && isSuccess(res)) setDetailData(res.data as any);
    else setDetailRow(null);
  };

  const handleCloseDetailModal = () => {
    setDetailRow(null);
    setDetailData(undefined);
  };

  /**
   * Cấp tài khoản mới, hoặc đặt lại mật khẩu cho khách đã có tài khoản.
   *
   * Trả kết quả về cho modal để nó hiện mật khẩu tạm. KHÔNG đóng modal ở đây:
   * mật khẩu chỉ về đúng một lần, đóng sớm là mất, phải đặt lại mật khẩu mới có cái khác.
   */
  const handleAccountSubmit = async (values: {
    username: string;
    password?: string;
  }): Promise<CustomerAccountResult | null> => {
    if (!accountRow) return null;

    const { row, mode } = accountRow;

    // Thiếu accountId mà vẫn ở chế độ đặt lại thì DỪNG, không được rơi sang cấp mới:
    // rơi sang cấp mới sẽ nhận lỗi "Tên đăng nhập đã tồn tại" — thông báo không liên quan
    // gì tới việc nhân viên đang làm, rất khó hiểu.
    if (mode === "reset" && !accountRow.accountId) {
      showToast({
        message: "Không xác định được tài khoản của khách. Vui lòng tải lại trang.",
        type: "error",
      });
      return null;
    }

    const res =
      mode === "reset" && accountRow.accountId
        ? await resetAccountPassword(Number(accountRow.accountId), values.password)
        : await issueAccount({
            customer_id: row.id,
            username: values.username,
            password: values.password,
            // Chép từ hồ sơ khách để app hiển thị nhanh; nguồn sự thật vẫn là customer service.
            name: row.name,
            phone: row.phone,
          });

    if (!res || !isSuccess(res)) {
      showToast({
        message:
          (res as any)?.message ||
          (mode === "reset" ? "Đặt lại mật khẩu thất bại" : "Cấp tài khoản thất bại"),
        type: "error",
      });
      return null;
    }

    showToast({
      message: mode === "reset" ? "Đã đặt lại mật khẩu" : "Đã cấp tài khoản app cho khách",
      type: "success",
    });

    // Cấp mới xong thì dòng đó phải chuyển sang nút "Đặt lại mật khẩu" ngay,
    // không đợi tải lại cả danh sách.
    if (mode === "issue") {
      const created = res.data;
      setAccountMap((prev) =>
        new Map(prev ?? []).set(String(row.id), {
          id: created.id,
          customer_id: created.customer_id,
          username: created.username,
          status: 1,
          must_change_password: 1,
        }),
      );
    }

    return res.data;
  };
  // RENDER
  return (
    <div className="">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-green-600 pl-3">
          Danh sách Khách Hàng
        </h1>
        <div className="flex gap-3">
          <button
            onClick={() => setOpenImport(true)}
            className="group inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition-all hover:bg-blue-700 hover:-translate-y-0.5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <span>NHẬP EXCEL</span>
          </button>
          <button
            onClick={() => setOpenCreate(true)}
            className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
              <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
            </svg>
            <span>THÊM KHÁCH HÀNG</span>
          </button>
        </div>
      </div>

      {/* Tabs (trái) + bộ lọc (phải) chung một thanh */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-lg bg-slate-50 px-6 py-3">
        <div className="flex flex-wrap gap-6">
        {[
          { key: "all", label: "TẤT CẢ" },
          { key: "active", label: "HOẠT ĐỘNG" },
          { key: "inactive", label: "NGƯNG HOẠT ĐỘNG" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key as any)}
            className={`relative pb-2 text-sm font-semibold uppercase tracking-wide ${
              tab === t.key
                ? "border-b-2 border-green-500 text-green-600"
                : "text-slate-400 hover:text-slate-600"
            }`}
          >
            {t.label}
            <span
              className={`ml-2 rounded-full px-2 py-0.5 ${
                t.key === "active"
                  ? "bg-green-100 text-green-600"
                  : t.key === "inactive"
                  ? "bg-red-100 text-red-600"
                  : "bg-slate-200 text-slate-600"
              }`}
            >
              {t.key === "active"
                ? activeCount
                : t.key === "inactive"
                ? inactiveCount
                : totalCount}
            </span>
          </button>
        ))}
        </div>

        <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
          <div className="relative w-full sm:w-[300px]">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 10.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z" />
              </svg>
            </div>
            <input
              value={keySearch}
              onChange={(e) => setKeySearch(e.target.value)}
              placeholder="Tìm theo mã, tên, SĐT, địa chỉ..."
              className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all"
            />
          </div>

          <CustomerTypeSelect
            value={customerTypeFilter}
            onChange={(val) => setCustomerTypeFilter(val)}
            showLabel={false}
          />
        </div>
      </div>


      {/* Table */}
      <CustomerTable
        customers={filteredRows}
        loading={loadingList}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(n) => { setLimit(n); setPage(1); }}
        onEdit={setEditRow}
        onViewDetail={handleViewDetail}
        onOpenSlaConfig={setSlaConfigRow}
        accountCustomerIds={accountMap && new Set(accountMap.keys())}
        slaOverrideCustomerIds={slaOverrideIds}
        onIssueAccount={(row) => setAccountRow({ row, mode: "issue" })}
        onResetAccountPassword={(row) => {
          const account = accountMap?.get(String(row.id));
          if (!account) return;
          setAccountRow({
            row,
            mode: "reset",
            accountId: account.id,
            username: account.username,
          });
        }}
      />

      {/* Modals */}
      {openCreate && (
        <CustomerCreateModal
          onClose={() => setOpenCreate(false)}
          onSubmit={onCreate}
          isSubmitting={creating}
        />
      )}

      {editRow && (
        <CustomerCreateModal
          onClose={() => setEditRow(null)}
          onSubmit={onEditSubmit}
          isSubmitting={updating}
          titleText="Cập nhật khách hàng"
          submitText="Cập nhật"
          initialValues={{
            name: editRow.name,
            phone: editRow.phone,
            email: editRow.email ?? "",
            address: editRow.address,
            type_customer:
              (editRow as any).type_customer ?? CustomerType.ENTERPRISE,
            isActive: editRow.statusCode === CustomerStatusCode.ACTIVE,
            discount_percent: Number((editRow as any).discount_percent ?? 0),
            default_payment_method: Number((editRow as any).default_payment_method ?? 1),
            requires_counting: Number((editRow as any).requires_counting ?? 0),
            sale_id: (editRow as any).sale_id ?? null,
            code: (editRow as any).code ?? "",
            lat: (editRow as any).lat ?? null,
            lng: (editRow as any).lng ?? null,
          }}
        />
      )}

      <CustomerImportModal
        isOpen={openImport}
        onClose={() => setOpenImport(false)}
        onSuccess={() => void load()}
      />

      {detailRow && detailData && (
        <CustomerDetailModal
          name={detailRow.name}
          data={detailData}
          saleName={detailData.sale_id ? usersMap.get(Number(detailData.sale_id)) : undefined}
          loading={loadingDetail}
          onClose={handleCloseDetailModal}
        />
      )}

      {slaConfigRow && (
        <CustomerSlaConfigModal
          customerId={slaConfigRow.id}
          customerName={slaConfigRow.name}
          onClose={() => setSlaConfigRow(null)}
          onSaved={(customerId, hasOverride) => {
            setSlaOverrideIds((prev) => {
              const next = new Set(prev ?? []);
              if (hasOverride) next.add(String(customerId));
              else next.delete(String(customerId));
              return next;
            });
          }}
        />
      )}

      {accountRow && (
        <CustomerAccountModal
          mode={accountRow.mode}
          customerName={accountRow.row.name}
          customerPhone={accountRow.row.phone}
          existingUsername={accountRow.username}
          isSubmitting={issuingAccount || resettingAccount}
          onClose={() => setAccountRow(null)}
          onSubmit={handleAccountSubmit}
        />
      )}
    </div>
  );
};

export default CustomerIndexPage;
